import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  FaMapMarkerAlt, FaBriefcase, FaUser, FaBuilding, FaClock, 
  FaCertificate, FaArrowLeft, FaEnvelope, FaPhone, FaEuroSign,
  FaCalendar, FaGlobe, FaCheckCircle, FaPaperPlane, FaEye,
  FaHardHat, FaStar, FaTools
} from 'react-icons/fa';

export default function TalentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, user } = useContext(AppContext);
  
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState([]);
  const [certifications, setCertifications] = useState([]);
  
  // Application form
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [applyPhone, setApplyPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAd();
    fetchReferenceData();
  }, [id]);

  const fetchAd = async () => {
    try {
      const response = await axios.get(`${API}/talents/${id}`);
      setAd(response.data);
    } catch (error) {
      console.error('Error fetching ad:', error);
      toast.error('Annonce non trouvée');
      navigate('/talents');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [specRes, certRes] = await Promise.all([
        axios.get(`${API}/talents/specialties`),
        axios.get(`${API}/talents/certifications`)
      ]);
      setSpecialties(specRes.data);
      setCertifications(certRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const getSpecialtyName = (id) => {
    const spec = specialties.find(s => s.id === id);
    return spec ? spec.name_fr : id;
  };

  const getCertificationName = (id) => {
    const cert = certifications.find(c => c.id === id);
    return cert ? cert.name : id;
  };

  const getContractLabel = (type) => {
    const labels = {
      cdi: 'CDI',
      cdd: 'CDD',
      interim: 'Intérim',
      freelance: 'Freelance / Indépendant',
      stage: 'Stage'
    };
    return labels[type] || type;
  };

  const getExperienceLabel = (level) => {
    const labels = {
      debutant: 'Débutant (0-2 ans)',
      junior: 'Junior (2-5 ans)',
      confirme: 'Confirmé (5-10 ans)',
      senior: 'Senior (10-15 ans)',
      expert: 'Expert (15+ ans)'
    };
    return labels[level] || level;
  };

  const getMobilityLabel = (mobility) => {
    const labels = {
      local: 'Local uniquement',
      regional: 'Régional',
      national: 'National',
      international: 'International'
    };
    return labels[mobility] || mobility;
  };

  const getAvailabilityLabel = (availability) => {
    const labels = {
      immediate: 'Immédiate',
      date: 'À partir d\'une date',
      negotiable: 'À négocier'
    };
    return labels[availability] || availability;
  };

  const formatSalary = () => {
    if (!ad.salary_min && !ad.salary_max) return null;
    
    const typeLabels = {
      hourly: '/h',
      daily: '/jour',
      monthly: '/mois',
      yearly: '/an'
    };
    const suffix = typeLabels[ad.salary_type] || '';
    
    if (ad.salary_min && ad.salary_max) {
      return `${ad.salary_min.toLocaleString()}€ - ${ad.salary_max.toLocaleString()}€${suffix}`;
    }
    if (ad.salary_min) return `À partir de ${ad.salary_min.toLocaleString()}€${suffix}`;
    if (ad.salary_max) return `Jusqu'à ${ad.salary_max.toLocaleString()}€${suffix}`;
    return null;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleApply = async () => {
    if (!user) {
      toast.error('Connectez-vous pour postuler');
      navigate('/login');
      return;
    }

    if (!applyMessage.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/talents/${id}/apply?user_id=${user.id}&message=${encodeURIComponent(applyMessage)}&phone=${encodeURIComponent(applyPhone)}`);
      toast.success('Candidature envoyée avec succès !');
      setShowApplyForm(false);
      setApplyMessage('');
      setApplyPhone('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!ad) return null;

  const isOwnAd = user && user.id === ad.user_id;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className={`py-12 ${ad.ad_type === 'job_offer' ? 'bg-gradient-to-br from-[#635BFF] to-[#8B7DFF]' : 'bg-gradient-to-br from-[#00D4FF] to-[#0099CC]'}`}>
        <div className="container mx-auto px-4">
          <Link to="/talents" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
            <FaArrowLeft /> Retour aux annonces
          </Link>

          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Icon */}
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              {ad.ad_type === 'job_offer' ? (
                <FaBuilding className="text-4xl text-white" />
              ) : (
                <FaUser className="text-4xl text-white" />
              )}
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge className="bg-white/20 text-white border-0">
                  {ad.ad_type === 'job_offer' ? 'Offre d\'emploi' : 'Profil disponible'}
                </Badge>
                {ad.contract_types?.map(ct => (
                  <Badge key={ct} className="bg-white text-[#0A2540]">{getContractLabel(ct)}</Badge>
                ))}
                {ad.is_featured && (
                  <Badge className="bg-yellow-400 text-yellow-900">
                    <FaStar className="mr-1" /> Premium
                  </Badge>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {ad.title}
              </h1>

              <p className="text-xl text-white/90 mb-4">
                {ad.ad_type === 'job_offer' ? ad.company_name : ad.user_name}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-white/80">
                {ad.location && (
                  <span className="flex items-center gap-2">
                    <FaMapMarkerAlt /> {ad.location}{ad.region ? `, ${ad.region}` : ''}
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <FaClock /> Publiée le {formatDate(ad.created_at)}
                </span>
                <span className="flex items-center gap-2">
                  <FaEye /> {ad.views_count} vues
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">
                  {ad.description}
                </div>
              </CardContent>
            </Card>

            {/* Specialties */}
            {ad.specialties?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaTools className="text-[#635BFF]" /> Spécialités
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {ad.specialties.map(spec => (
                      <Badge key={spec} variant="outline" className="text-base py-2 px-4">
                        {getSpecialtyName(spec)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {(ad.certifications?.length > 0 || ad.other_certifications) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaCertificate className="text-[#00D4FF]" /> Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {ad.certifications?.map(cert => (
                      <Badge key={cert} className="bg-green-100 text-green-700 text-sm py-1.5 px-3">
                        <FaCheckCircle className="mr-1" /> {getCertificationName(cert)}
                      </Badge>
                    ))}
                  </div>
                  {ad.other_certifications && (
                    <p className="mt-4 text-gray-600">
                      <strong>Autres :</strong> {ad.other_certifications}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="sticky top-24">
              <CardContent className="p-6">
                {!isOwnAd && (
                  <>
                    {!showApplyForm ? (
                      <Button 
                        onClick={() => user ? setShowApplyForm(true) : navigate('/login')}
                        className={`w-full mb-4 py-6 text-lg ${ad.ad_type === 'job_offer' ? 'bg-[#635BFF] hover:bg-[#5246E5]' : 'bg-[#00D4FF] hover:bg-[#00B8D9]'}`}
                      >
                        <FaPaperPlane className="mr-2" />
                        {ad.ad_type === 'job_offer' ? 'Postuler' : 'Contacter'}
                      </Button>
                    ) : (
                      <div className="space-y-4 mb-6">
                        <Textarea
                          value={applyMessage}
                          onChange={(e) => setApplyMessage(e.target.value)}
                          placeholder="Écrivez votre message de candidature..."
                          rows={5}
                        />
                        <Input
                          value={applyPhone}
                          onChange={(e) => setApplyPhone(e.target.value)}
                          placeholder="Votre téléphone (optionnel)"
                        />
                        <div className="flex gap-2">
                          <Button 
                            onClick={handleApply}
                            disabled={submitting}
                            className={`flex-1 ${ad.ad_type === 'job_offer' ? 'bg-[#635BFF]' : 'bg-[#00D4FF]'}`}
                          >
                            {submitting ? 'Envoi...' : 'Envoyer'}
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setShowApplyForm(false)}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {isOwnAd && (
                  <Link to={`/user/mes-annonces-talents`}>
                    <Button className="w-full mb-4" variant="outline">
                      Gérer mes annonces
                    </Button>
                  </Link>
                )}

                {/* Info Grid */}
                <div className="space-y-4">
                  {/* Experience */}
                  {ad.experience_level && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FaHardHat className="text-[#635BFF]" />
                      <div>
                        <p className="text-xs text-gray-500">Expérience</p>
                        <p className="font-medium">{getExperienceLabel(ad.experience_level)}</p>
                      </div>
                    </div>
                  )}

                  {/* Salary */}
                  {formatSalary() && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FaEuroSign className="text-green-600" />
                      <div>
                        <p className="text-xs text-gray-500">Rémunération</p>
                        <p className="font-medium">{formatSalary()}</p>
                        {ad.salary_negotiable && <p className="text-xs text-gray-400">Négociable</p>}
                      </div>
                    </div>
                  )}

                  {/* Availability */}
                  {ad.availability && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FaCalendar className="text-[#00D4FF]" />
                      <div>
                        <p className="text-xs text-gray-500">Disponibilité</p>
                        <p className="font-medium">{getAvailabilityLabel(ad.availability)}</p>
                        {ad.start_date && <p className="text-xs text-gray-400">À partir du {ad.start_date}</p>}
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  {ad.duration && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FaClock className="text-orange-500" />
                      <div>
                        <p className="text-xs text-gray-500">Durée</p>
                        <p className="font-medium">{ad.duration}</p>
                      </div>
                    </div>
                  )}

                  {/* Mobility */}
                  {ad.mobility && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FaGlobe className="text-blue-500" />
                      <div>
                        <p className="text-xs text-gray-500">Mobilité</p>
                        <p className="font-medium">{getMobilityLabel(ad.mobility)}</p>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {ad.location && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FaMapMarkerAlt className="text-red-500" />
                      <div>
                        <p className="text-xs text-gray-500">Localisation</p>
                        <p className="font-medium">{ad.location}</p>
                        {ad.region && <p className="text-xs text-gray-400">{ad.region}, {ad.country}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Info (for ad owner) */}
                {isOwnAd && (
                  <div className="mt-6 pt-6 border-t">
                    <p className="text-sm text-gray-500 mb-2">Coordonnées affichées :</p>
                    {ad.user_email && (
                      <p className="flex items-center gap-2 text-sm">
                        <FaEnvelope className="text-gray-400" /> {ad.user_email}
                      </p>
                    )}
                    {ad.user_phone && (
                      <p className="flex items-center gap-2 text-sm mt-1">
                        <FaPhone className="text-gray-400" /> {ad.user_phone}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
